import React, { useEffect, useRef } from 'react';
import {
  Assets,
  Colors,
  FontFamily,
  Input,
  Main,
  Models,
  Pagination,
  PrimaryButton,
} from 'utils/imports.utils';
import { ButtonSize, ButtonTypes, ProjectStatus } from 'utils/interface.utils';
import './welcome.screen.scss';
import { useNavigate } from 'react-router-dom';
import { getStoredUser, toastifyError, toastifySuccess, useSetState } from 'utils/functions.utils';
import ProjectModal from 'components/project_modal/project_modal.component';
import ConfirmationModal from 'components/configuration_modal/configuration_modal.component';
import { useDispatch } from 'react-redux';
import useDebounce from 'utils/debounce.utils';
import { setProject } from '../../store/reducers/projectSlice';
import ProjectList from 'components/project_list/project_list.component';
import SearchSortField from 'components/search_sort_field/search_sort_field.component';
import { PROJECT_PAGE_SIZE, SORT_TYPES } from 'utils/constant.utils';
import { STRINGS } from 'constants/strings.constant';
import { Project, ProjectStatusCounts, ProjectStatusFilter } from 'interfaces/project.interface';
import StatusFilterPills from 'common_components/ui/status_filter_pills/status_filter_pills.ui';
import { setSelectedTargetImageID, setSelectedObjectID, setCurrentSelectedPropertyType, setIsTargetImageListItemClicked, setZoomPercentage, setSelectedTargetIndex, setTargetImageDataList, setSelectedTransformControl, setIsObjectListItemClicked, setIsUpdatingFromEditorWorkspace, setLastDeletedTargetImageData } from 'store/reducers/editorSlice';

// Projects start sorted by when they were last changed; the other sorts are the user's choice.
const DEFAULT_SORT = SORT_TYPES[2];

const Welcome = () => {
  const [state, setState] = useSetState({
    isProjectModalOpen: false,
    currentProjectId: '',
    // null = not fetched yet; an array (possibly empty) = fetched.
    projectList: null,
    isConfirmationModalOpen: false,
    projectToDelete: null,
    filteredProjectSearchText: '',
    filteredProjectSortText: DEFAULT_SORT,
    statusFilter: 'all',
    statusCounts: null,
    // 1-based; taken from the server's answer, so it only moves when a page was actually loaded.
    page: 1,
    totalDocs: null,
    isLoading: false,
    // Whether the list on screen came from a search, a status pill or a non-default sort.
    isListFiltered: false,
  });
  const navigate = useNavigate();
  // Only the newest list request may update the screen, so a slow older one cannot overwrite it.
  const latestRequest = useRef(0);
  const dispatch = useDispatch();

  const storedUser = getStoredUser();

  const handleCreate = () => {
    setState({ isProjectModalOpen: false, currentProjectId: '' });
    navigate('/editor');
  };

  const handleCreateNewProjectModalOpen = () => {
    setState({
      isProjectModalOpen: true,
    });
  };
  const handleProjectClick = (proj: any) => {
    dispatch(
      setProject({
        project_id: proj._id,
        project_name: proj.project_name,
      }),
    );

    if (proj.project_status === ProjectStatus.PUBLISHED) {
      navigate(`/preview/${proj._id}`);
    } else {
      navigate(`/editor`);
    }
  };

  // "All" is the absence of a status: the server only filters when one is sent.
  const statusParam = (status: ProjectStatusFilter): string | undefined =>
    status === 'all' ? undefined : status;

  const debouncedSearch = useDebounce((value, sortBy, status) => {
    const body = {
      user_id: storedUser._id.toString(),
      search: value.trim(),
      sortBy: sortBy,
      status: statusParam(status),
    };
    getManyProjectsByUser(body);
  }, 500);

  const handleProjectSearchAndFilter = (search, sortBy) => {
    setState({
      filteredProjectSearchText: search,
      filteredProjectSortText: sortBy,
    });
    debouncedSearch(search, sortBy?.value === sortBy?.ALL ? '' : sortBy?.value ?? '', state.statusFilter);
  };

  const fetchCurrentList = (status: ProjectStatusFilter, page = 1) =>
    getManyProjectsByUser({
      user_id: storedUser._id.toString(),
      search: state.filteredProjectSearchText.trim(),
      sortBy: state.filteredProjectSortText?.value ?? '',
      status: statusParam(status),
      skip: (page - 1) * PROJECT_PAGE_SIZE,
    });

  // A new status, search or sort starts again from the first page.
  const handleStatusChange = (status: ProjectStatusFilter) => {
    setState({ statusFilter: status });
    fetchCurrentList(status);
  };

  // The list is shorter than the page you were reading, so start the new page from the top.
  const handlePageChange = (page: number) => {
    window.scrollTo(0, 0);
    fetchCurrentList(state.statusFilter, page);
  };

  const getManyProjectsByUser = async (
    body: {
      user_id: string;
      search?: string;
      sortBy?: string;
      status?: string;
      skip?: number;
    },
    isInitialLoad = false,
  ) => {
    const requestId = ++latestRequest.current;
    try {
      setState({ isLoading: true });
      const skip = body.skip ?? 0;
      const res: any = await Models.auth.getManyProject({ ...body, skip, limit: PROJECT_PAGE_SIZE });
      if (requestId !== latestRequest.current) return;
      const docs: Project[] = res.data.docs;
      const totalDocs: number = res.data.totalDocs ?? docs.length;
      // The page was emptied (its rows were deleted): go to the last page that still has rows.
      if (docs.length === 0 && totalDocs > 0 && skip > 0) {
        const lastPageSkip = (Math.ceil(totalDocs / PROJECT_PAGE_SIZE) - 1) * PROJECT_PAGE_SIZE;
        if (lastPageSkip < skip) {
          getManyProjectsByUser({ ...body, skip: lastPageSkip });
          return;
        }
      }
      setState({
        projectList: docs,
        totalDocs,
        page: Math.floor((res.data.skip ?? skip) / PROJECT_PAGE_SIZE) + 1,
        // An older server sends no counts: show what came back rather than zeros.
        statusCounts: res.data.status_counts ?? { all: docs.length, live: 0, ready: 0, needs: 0 },
        isListFiltered: !!body.search?.trim() || !!body.status || (!!body.sortBy && body.sortBy !== DEFAULT_SORT.value),
        isLoading: false,
      });
    } catch (err) {
      if (requestId !== latestRequest.current) return;
      console.error('Failed to get projects', err);
      // A failed first load falls back to an empty list so the page does not stay on the loader;
      // a failed search keeps the rows already on screen.
      setState(
        isInitialLoad
          ? { projectList: [], isLoading: false }
          : { isLoading: false },
      );
    }
  };

  const projectDelete = async (projectId: string) => {
    try {
      await Models.auth.deleteProject({ project_id: projectId });
      setState({
        projectList: (state.projectList ?? []).filter(
          (proj: any) => proj._id !== projectId,
        ),
        isConfirmationModalOpen: false,
        projectToDelete: null,
      });
      toastifySuccess('Deleted Successfully!');
      // The pill counts changed too, and only the server knows the new ones.
      fetchCurrentList(state.statusFilter, state.page);
    } catch (err) {
      toastifyError('Failed to delete project');
      console.error('Failed to delete project', err);
    }
  };

  const projects: Project[] = state.projectList ?? [];
  const isFirstLoad = state.projectList === null;
  const statusCounts: ProjectStatusCounts = state.statusCounts ?? { all: projects.length, live: 0, ready: 0, needs: 0 };
  // The first-project screen is for an empty list that was fetched without any filter. It is decided from how the
  // list on screen was fetched, not from the live input: clearing a search that matched nothing empties the input
  // at once, while the list on screen is still that empty result until the reload returns.
  const hasNoProjects = !isFirstLoad && projects.length === 0 && !state.isListFiltered;

  const totalPages = Math.ceil((state.totalDocs ?? projects.length) / PROJECT_PAGE_SIZE);

  const hasProjects = projects.length > 0;
  // The column header and footer only make sense above/below real rows, not the spinner or the empty message.
  const showTableChrome = hasProjects && !state.isLoading;

  useEffect(() => {
    const body = {
      user_id: storedUser._id,
      search: '',
      sortBy: DEFAULT_SORT.value,
    };
    getManyProjectsByUser(body, true);
  }, []);

  useEffect(() => {
    return () => {
      dispatch(setTargetImageDataList([]))
      dispatch(setCurrentSelectedPropertyType(null))
      dispatch(setSelectedTargetIndex(null))
      dispatch(setSelectedTargetImageID(null))
      dispatch(setSelectedObjectID(null))
      dispatch(setZoomPercentage(100))
      dispatch(setSelectedTransformControl(null))
      dispatch(setLastDeletedTargetImageData(null))
      dispatch(setIsTargetImageListItemClicked(false))
      dispatch(setIsObjectListItemClicked(false))
      dispatch(setIsUpdatingFromEditorWorkspace(false))
    }
  })

  return (
    <>
      {isFirstLoad ? (
        <Main
          backgroundColor={Colors.screenBackground}
          isRenderOnlyLogo={false}>
          <div className="loader_container">
            <div className="spinner"></div>
          </div>
        </Main>
      ) : hasNoProjects ? (
        <Main
          backgroundColor={Colors.screenBackground}
          isRenderOnlyLogo={false}>
          <div className="welcome">
            <div className="welcome_container">
              <div className="welcome_illustration_container">
                <img
                  src={Assets.welcome_illustration}
                  alt="welcome_illustration"
                />
              </div>
              <div className="welcome_content_container">
                <div className="welcome_content_title">
                  {STRINGS.brand.welcome}
                </div>
                <div className="welcome_content_desc">
                  Scan the target image to unlock the AR module. Prepare for an
                  immersive experience.
                </div>
              </div>
              <div className="welcome_button_container">
                <PrimaryButton
                  text="Create new project"
                  left_icon={Assets.plus}
                  iconSize={{ width: 16, height: 16 }}
                  type={ButtonTypes.PRIMARY_GRADIENT}
                  size={ButtonSize.LARGE}
                  onClick={handleCreateNewProjectModalOpen}
                  lineHeight="28px"
                  fontFamily={FontFamily.regular}
                />
              </div>
            </div>
          </div>
        </Main>
      ) : (
        <Main
          backgroundColor={Colors.screenBackground}
          isRenderOnlyLogo={false}
          isAdmin>
          <div className="project">
            <div className="project_container">
              <div className="project_content_container">
                <div className="project_content_title">{STRINGS.brand.name}</div>
                <div className="project_content_desc">
                  {STRINGS.project.studioTagline}
                </div>
              </div>
              <div className="project_button_container">
                <PrimaryButton
                  text="Create new project"
                  left_icon={Assets.plus}
                  iconSize={{ width: 16, height: 16 }}
                  type={ButtonTypes.PRIMARY_GRADIENT}
                  size={ButtonSize.LARGE}
                  onClick={handleCreateNewProjectModalOpen}
                  lineHeight="28px"
                  fontFamily={FontFamily.regular}
                />
              </div>
            </div>
            <div className="project_image_container">
              <div className="project_image_container_title active">
                {STRINGS.project.imageBasedAr}
                <span className="project_image_container_count">{statusCounts.all}</span>
              </div>
            </div>

            <div className="project_actions">
              <div className="project_actions_status">
                <StatusFilterPills
                  counts={statusCounts}
                  value={state.statusFilter}
                  onChange={handleStatusChange}
                />
              </div>
              <SearchSortField
                searchValue={state.filteredProjectSearchText}
                onSearchChange={(text) => handleProjectSearchAndFilter(text, state.filteredProjectSortText)}
                sortOptions={SORT_TYPES}
                sortValue={state.filteredProjectSortText}
                onSortChange={(option) => handleProjectSearchAndFilter(state.filteredProjectSearchText, option)}
              />
            </div>
            <div
              className="project_list"
              role={showTableChrome ? 'table' : undefined}
              aria-label={showTableChrome ? STRINGS.project.imageBasedAr : undefined}>
              {showTableChrome && (
                <div className="project_list_header" role="row">
                  <span role="columnheader">{STRINGS.project.columns.project}</span>
                  <span role="columnheader">{STRINGS.project.columns.status}</span>
                  <span role="columnheader">{STRINGS.project.columns.targets}</span>
                  <span role="columnheader">{STRINGS.project.columns.lastEdited}</span>
                  <span role="columnheader" className="project_list_header_actions">{STRINGS.project.columns.actions}</span>
                </div>
              )}
              {state.isLoading ? (
                <div className="filter_load">
                  <div className="spin"></div>
                </div>
              ) : hasProjects ? (
                projects.map((proj) => (
                  <ProjectList
                    key={proj._id}
                    project={proj}
                    handleProjectClick={handleProjectClick}
                    handleDeleteClick={(e, id) => {
                      e.stopPropagation();
                      setState({
                        isConfirmationModalOpen: true,
                        projectToDelete: id,
                      });
                    }}
                  />
                ))
              ) : (
                <div className="project_list_no_projects">
                  No projects found
                </div>
              )}
            </div>
            {showTableChrome && totalPages > 1 && (
              <div className="project_pagination">
                <Pagination
                  currentPage={state.page}
                  totalPages={totalPages}
                  pageSize={PROJECT_PAGE_SIZE}
                  totalItems={state.totalDocs}
                  onPageChange={handlePageChange}
                  variant="pill"
                />
              </div>
            )}
            {showTableChrome && totalPages <= 1 && (
              <div className="project_list_footer">
                Showing {projects.length} of {statusCounts.all} projects
              </div>
            )}
          </div>
        </Main>
      )}

      {/* Project Modal */}
      {state.isProjectModalOpen && (
        <ProjectModal
          closeModal={() => setState({ isProjectModalOpen: false })}
          onCreate={handleCreate}
        />
      )}

      {/* Confirmation Modal */}
      {state.isConfirmationModalOpen && (
        <ConfirmationModal
          isDestructive
          highlightText={projects.find((proj) => proj._id === state.projectToDelete)?.project_name ?? STRINGS.project.deleteDialog.fallbackName}
          primaryText={STRINGS.project.deleteDialog.title}
          secondaryText={STRINGS.project.deleteDialog.message}
          acceptBtnText={STRINGS.project.deleteDialog.confirm}
          acceptHandle={() => projectDelete(state.projectToDelete)}
          rejectBtnText={STRINGS.project.deleteDialog.cancel}
          rejectHandle={() =>
            setState({ isConfirmationModalOpen: false, projectToDelete: null })
          }
          closeModal={() =>
            setState({ isConfirmationModalOpen: false, projectToDelete: null })
          }
        />
      )}
    </>
  );
};

export default Welcome;
